import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { transferServices } from '../transferServices'
import { SavingGoalTransferOutPayload } from '../transferTypes'
import { QUERY_KEYS } from '@/constants/queryKeys'

interface UseCreateGoalTransferOutOptions {
  onSuccess?: () => void
  onError?: (error: { errorMessage?: string; code?: string }) => void
}

export const useCreateGoalTransferOut = (
  options?: UseCreateGoalTransferOutOptions,
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation<void, Error, SavingGoalTransferOutPayload>({
    mutationFn: transferServices.createGoalTransferOut,
    onSuccess: () => {
      // Invalidate related queries like in React Native
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SAVINGS_GOAL],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SAVING_GOALS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.WALLET],
      })

      // Remove transaction queries to force refetch
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
      })
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.SAVINGS_GOAL_HISTORY],
      })

      if (options?.onSuccess) {
        options.onSuccess()
      }
    },
    onError: (error: any) => {
      console.error('Transfer out error:', error)

      let errorMessage = t('api:errors.somethingWrong')
      let code = 'UNKNOWN_ERROR'

      // Handle specific error codes like in React Native
      if (error?.response?.data?.code) {
        code = error.response.data.code
        if (code === 'SAVING_GOAL_TRANSFER_OUT_AMOUNT_EXCEEDS_STAKED_AMOUNT') {
          errorMessage = t('api:amountExceedsBalance')
        }
      }

      if (options?.onError) {
        options.onError({ errorMessage, code })
      } else {
        // Show default error toast if no custom error handler
        toast.error(errorMessage)
      }
    },
  })

  return {
    ...mutation,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  }
}
